import React, { FC, useState } from 'react';
import { X, Settings, User, LogOut, Info, HelpCircle, Bell, Languages } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { navigateTo, Screen } from '../../store/slices/navigationSlice';
import { SidebarThemeToggle } from './ThemeToggle';
import { LogoutModal, useLogout } from './LogoutModal';
import { LanguageSelectionModal } from './LanguageSelectionModal';
import { useLanguage } from '../../contexts/LanguageContext';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Sidebar: FC<SidebarProps> = ({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const { showLogoutModal, triggerLogout, closeLogoutModal } = useLogout();
  const [showAbout, setShowAbout] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const { t } = useLanguage();


  useAppSelector((state) => state.navigation.currentScreen);
  const handleNavigation = (screenId: Screen) => {
      dispatch(navigateTo(screenId));
  };

  //About
  const handleShowAbout = () => {
    setShowAbout(true);
  }

  const handleCloseAbout = () => {
    setShowAbout(false);
    onClose();
  };


  //Language
  const handleShowLanguage = () => {
    setShowLanguageModal(true);
  };

  const handleCloseLanguage = () => {
    setShowLanguageModal(false);
  };

  //Logout
  const handleLogoutClick = () => {
    triggerLogout();
  };
  return (
    <div
      className={`fixed inset-0 z-60 transform ${isOpen ? 'translate-x-0' : 'translate-x-full'} transition-transform duration-300 ease-in-out`}
    >
      <div className="absolute inset-0 bg-gray-900 opacity-20 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-64 bg-card h-full shadow-xl border-l border-border flex flex-col ml-auto">
        <div className="p-4 flex justify-end bg-primary text-primary-foreground">
          <button onClick={onClose} className="text-primary-foreground hover:text-primary-foreground/80 p-1 rounded-md hover:bg-primary-foreground/10 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>
        <nav className="flex-1 px-4 py-3 space-y-1">
          {/* Main Navigation Links */}
          <button 
              onClick={() => handleNavigation('profile')}
              className="w-full flex items-center p-3 text-card-foreground rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <User className="w-5 h-5 mr-3" />
              <span className="font-medium">{t('sidebar.profile')}</span>
          </button>
          <a href="#" className="flex items-center p-3 text-card-foreground rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors">
            <Settings className="w-5 h-5 mr-3" />
            <span className="font-medium">{t('sidebar.settings')}</span>
          </a>
          {/* Add more navigation links here based on your app's routes */}

          {/* Preferences */}
          <div className="pt-4 mt-4 border-t border-border">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-3 px-3 tracking-wider">{t('sidebar.preferences')}</h3>
            <a href="#" className="flex items-center p-3 text-card-foreground rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors">
              <Bell className="w-5 h-5 mr-3" />
              <span className="font-medium">{t('sidebar.notifications')}</span>
            </a>
            <SidebarThemeToggle />
            <button 
              onClick={handleShowLanguage}
              className="w-full flex items-center p-3 text-card-foreground rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <Languages className="w-5 h-5 mr-3" />
              <span className="font-medium">{t('sidebar.language')}</span>
            </button>
          </div>

          {/* Help & About */}
          <div className="pt-4 mt-4 border-t border-border">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-3 px-3 tracking-wider">{t('sidebar.help_section')}</h3>
            <button
              onClick={() => handleNavigation('help')}
              className="w-full flex items-center p-3 text-card-foreground rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <HelpCircle className="w-5 h-5 mr-3" />
              <span className="font-medium">{t('sidebar.help')}</span>
            </button>
            <button 
              onClick={handleShowAbout}
              className="w-full flex items-center p-3 text-card-foreground rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Info className="w-5 h-5 mr-3" />
              <span className="font-medium">{t('sidebar.about')}</span>
            </button>
          </div>

          {/* Other sections like History, Archive, Share, Rate Us */}
          {/* <div className="pt-4 mt-4 border-t border-gray-200">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-2">More</h3>
            <a href="#" className="flex items-center p-2 text-muted-foreground rounded-lg hover:bg-gray-100">
              <History className="w-5 h-5 mr-3" />
              History
            </a>
            <a href="#" className="flex items-center p-2 text-muted-foreground rounded-lg hover:bg-gray-100">
              <Archive className="w-5 h-5 mr-3" />
              Archived Items
            </a>
            <a href="#" className="flex items-center p-2 text-muted-foreground rounded-lg hover:bg-gray-100">
              <Share2 className="w-5 h-5 mr-3" />
              Share App
            </a>
            <a href="#" className="flex items-center p-2 text-muted-foreground rounded-lg hover:bg-gray-100">
              <Star className="w-5 h-5 mr-3" />
              Rate Us
            </a>
          </div> */}

        </nav>
        <div className="text-center pb-4">
          <p className="text-xs text-muted-foreground">Tequisa Employee Tracker v1.0.0</p>
        </div>
        <div className="p-4 border-t border-border">
          <button 
            onClick={handleLogoutClick}
            className="w-full flex items-center p-3 text-destructive rounded-lg hover:bg-destructive/10 hover:text-destructive transition-colors"
          >
            <LogOut className="w-5 h-5 mr-3" />
            <span className="font-medium">{t('sidebar.logout')}</span>
          </button>
        </div>
      </div>

      {/* Logout Modal */}
      <LogoutModal 
        isOpen={showLogoutModal} 
        onClose={closeLogoutModal}
        onSuccess={onClose} // Close sidebar when logout succeeds
      />
      
      {/* Language Selection Modal */}
      <LanguageSelectionModal 
        isOpen={showLanguageModal} 
        onClose={handleCloseLanguage}
      />
      {showAbout && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-60 p-4">
          <div className="bg-card rounded-lg p-6 max-w-sm w-full border border-border shadow-xl">
            <h3 className="text-lg font-semibold mb-4 text-card-foreground">{t('sidebar.about')}</h3>
            <p className="text-muted-foreground mb-6">{t('sidebar.about_us')}</p>
            <div className="flex space-x-3">
              <button
                onClick={handleCloseAbout}
                className="flex-1 px-4 py-2 text-card-foreground bg-secondary rounded-lg hover:bg-secondary/80 transition-colors font-medium"
              >
                {t('common.buttons.ok')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
